import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import i18n from '../i18n';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { SafeHtml } from '../lib/sanitize';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ClipboardList, ArrowLeft, RefreshCw, Send, MessageCircle, Check, Loader2,
  Mail, MapPin, Building2, Euro, ShieldAlert, User, Clock, CheckCircle2, XCircle, Eye,
  FileText, Upload, Link as LinkIcon, Trash2,
} from 'lucide-react';

// ★ 2026-08-03 (Fase 31 C) — Ordini proposte.
// ★ 2026-09-04 (Fase 29) — Senior self-service completo: i Senior possono
// evadere i PROPRI ordini (email + allegati ad-hoc + WhatsApp). Gli admin
// CEO (Richard/Eufemia/Morena/Claudia) evadono tutti gli ordini.

const COUNTRY_LABEL = { albania: '🇦🇱 Albania', indonesia: '🇮🇩 Indonesia', thailandia: '🇹🇭 Thailandia' };
const PATH_LABEL = { equity: 'Equity', real_estate: 'Real Estate' };

const STATUS_META = {
  pending: { key: 'orders.stPending', fallback: 'In attesa', className: 'bg-slate-100 text-slate-700 border-slate-300' },
  partial: { key: 'orders.stPartial', fallback: 'In corso', className: 'bg-blue-50 text-blue-700 border-blue-300' },
  emails_sent: { key: 'orders.stEmailsSent', fallback: 'Email inviate', className: 'bg-indigo-50 text-indigo-700 border-indigo-300' },
  completed: { key: 'orders.stCompleted', fallback: 'Completato', className: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  cancelled: { key: 'orders.stCancelled', fallback: 'Annullato', className: 'bg-rose-50 text-rose-700 border-rose-300' },
};
const statusMeta = (status) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return { ...m, label: i18n.t(m.key, m.fallback) };
};
// Locale date/number in base alla lingua utente
const dtLocale = () => (i18n.language === 'es' ? 'es-ES' : i18n.language === 'en' ? 'en-GB' : 'it-IT');

// ═══════════════════════════════════════════════════════════════════════════
// LIST PAGE (/app/proposal-orders)
// ═══════════════════════════════════════════════════════════════════════════
export default function ProposalOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/proposal-orders', { params });
      setOrders(data.items || []);
      setIsAdmin(!!data.is_admin);
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('orders.loadListError', 'Errore caricamento ordini'));
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="proposal-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-[hsl(var(--primary))]" />
          <h1 className="text-2xl font-serif text-[hsl(var(--primary))]">{i18n.t('orders.title', 'Ordini proposte')}</h1>
          <Badge variant="outline" className="text-[10px]">{orders.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] h-9" data-testid="orders-status-filter">
              <SelectValue placeholder={i18n.t('orders.statusFilter', 'Filtro stato')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{i18n.t('orders.allStatuses', 'Tutti gli stati')}</SelectItem>
              <SelectItem value="pending">{i18n.t('orders.stPending', 'In attesa')}</SelectItem>
              <SelectItem value="partial">{i18n.t('orders.stPartial', 'In corso')}</SelectItem>
              <SelectItem value="emails_sent">{i18n.t('orders.stEmailsSent', 'Email inviate')}</SelectItem>
              <SelectItem value="completed">{i18n.t('orders.stCompleted', 'Completato')}</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={load} data-testid="orders-refresh">
            <RefreshCw className="w-4 h-4 mr-1" />{i18n.t('common.refresh', 'Aggiorna')}
          </Button>
        </div>
      </div>

      {!isAdmin && (
        <Alert className="border-amber-300 bg-amber-50/40">
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>{i18n.t('orders.myOrdersTitle', 'I tuoi ordini')}</AlertTitle>
          <AlertDescription>
            {i18n.t('orders.myOrdersDesc', 'Vedi solo gli ordini che hai creato tu. Come Senior puoi evaderli completamente: invio email, allegati ad-hoc e conferma WhatsApp.')}
          </AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : orders.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-muted-foreground">
            <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {i18n.t('orders.noOrders', 'Nessun ordine trovato.')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {orders.map((o) => {
            const meta = statusMeta(o.status);
            const totalItems = (o.items || []).length;
            const sentCount = (o.items || []).filter((it) => it.email_status === 'sent').length;
            const waCount = (o.items || []).filter((it) => it.whatsapp_done).length;
            return (
              <Link to={`/app/proposal-orders/${o.id}`} key={o.id} data-testid={`order-card-${o.id}`}>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[hsl(var(--primary))]">{o.lead_name || '—'}</span>
                        <Badge variant="outline" className={`text-[10px] ${meta.className}`}>
                          {meta.label}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{o.email_to || '—'}</span>
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{o.created_by_email}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(o.created_at).toLocaleString(dtLocale())}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <div className="text-right">
                        <div className="text-[10px] uppercase text-muted-foreground">{i18n.t('orders.proposals', 'Proposte')}</div>
                        <div className="font-semibold">{totalItems}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase text-muted-foreground">Email</div>
                        <div className="font-semibold">{sentCount}/{totalItems}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase text-muted-foreground">WhatsApp</div>
                        <div className="font-semibold">{waCount}/{totalItems}</div>
                      </div>
                      <div className="text-right border-l pl-3">
                        <div className="text-[10px] uppercase text-muted-foreground">{i18n.t('common.total', 'Totale')}</div>
                        <div className="font-bold text-emerald-700 flex items-center justify-end gap-0.5">
                          <Euro className="w-3 h-3" />{(o.total_price_eur || 0).toLocaleString(dtLocale())}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DETAIL PAGE (/app/proposal-orders/:id)
// ═══════════════════════════════════════════════════════════════════════════
export function ProposalOrderDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};   // riservato per uso futuro
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);   // proposal_id currently being processed
  const [previewItem, setPreviewItem] = useState(null);
  // ★ 2026-08-04 (Fase 33.2) — Send-email dialog con PDF override
  const [sendDialog, setSendDialog] = useState(null);   // item to send
  // ★ 2026-08-10 (Fase 36) — Multi-file ad-hoc
  const [adhocFiles, setAdhocFiles] = useState([]);
  const [adhocLinkInput, setAdhocLinkInput] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/proposal-orders/${id}`);
      setOrder(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('orders.loadError', 'Errore caricamento ordine'));
      if (err?.response?.status === 403 || err?.response?.status === 404) nav('/app/proposal-orders');
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const openSendDialog = (item) => {
    setSendDialog(item);
    setAdhocFiles([]);
    setAdhocLinkInput('');
  };

  const refreshOrder = async () => {
    try {
      const { data } = await api.get(`/proposal-orders/${id}`);
      setOrder(data);
      // Ricarica il dialog target dai nuovi dati
      if (sendDialog?.proposal_id) {
        const updated = (data.items || []).find((it) => it.proposal_id === sendDialog.proposal_id);
        if (updated) setSendDialog(updated);
      }
    } catch { /* noop */ }
  };

  const uploadAdhocNow = async (proposalId, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error(i18n.t('orders.notPdf', '"{{name}}" non è un PDF', { name: file.name }));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(i18n.t('orders.tooBig', '"{{name}}" troppo grande (max 10 MB)', { name: file.name }));
      return;
    }
    setPdfBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/proposal-orders/${id}/upload-adhoc-pdf/${proposalId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(i18n.t('orders.uploaded', '"{{name}}" caricato', { name: file.name }));
      await refreshOrder();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('orders.uploadError', 'Errore upload PDF'));
    } finally { setPdfBusy(false); }
  };

  const addAdhocLinkNow = async (proposalId) => {
    const url = (adhocLinkInput || '').trim();
    if (!url) return;
    setPdfBusy(true);
    try {
      await api.post(`/proposal-orders/${id}/set-adhoc-link/${proposalId}`, { pdf_ext_link: url });
      toast.success(i18n.t('orders.adhocLinkAdded', 'Link ad-hoc aggiunto'));
      setAdhocLinkInput('');
      await refreshOrder();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('common.error', 'Errore'));
    } finally { setPdfBusy(false); }
  };

  const removeAdhocItem = async (proposalId, pdfId) => {
    setPdfBusy(true);
    try {
      await api.delete(`/proposal-orders/${id}/adhoc-pdf/${proposalId}/${pdfId}`);
      toast.success(i18n.t('orders.attachmentRemoved', 'Allegato rimosso'));
      await refreshOrder();
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('orders.removeError', 'Errore rimozione'));
    } finally { setPdfBusy(false); }
  };

  const sendEmail = async (proposalId) => {
    setBusy(proposalId);
    try {
      const { data } = await api.post(`/proposal-orders/${id}/send-email/${proposalId}`);
      setOrder(data.order);
      const attCount = data.attachments_count || 0;
      const linkCount = data.ext_links_count || 0;
      const partsMsg = [];
      if (attCount > 0) partsMsg.push(i18n.t('orders.nAttachments', '{{count}} allegati', { count: attCount }));
      if (linkCount > 0) partsMsg.push(i18n.t('orders.nBodyLinks', '{{count}} link nel corpo', { count: linkCount }));
      const suffix = partsMsg.length > 0 ? ` · ${partsMsg.join(' + ')}` : '';
      if (data.real_send) {
        toast.success(`${i18n.t('orders.emailSentResend', 'Email inviata via Resend')}${suffix}`);
      } else {
        toast.warning(`${i18n.t('orders.emailLoggedOnly', 'Email registrata come inviata (Resend non attivo)')}${suffix}`);
      }
      if ((data.skipped_for_size || []).length > 0) {
        toast.warning(i18n.t('orders.skippedForSize', '{{count}} PDF saltati per limite 20MB totali', { count: data.skipped_for_size.length }));
      }
      setSendDialog(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('orders.sendError', 'Errore invio email'));
    } finally { setBusy(null); }
  };

  const toggleWhatsapp = async (proposalId, currentValue) => {
    setBusy(proposalId + '-wa');
    try {
      const newValue = !currentValue;
      const { data } = await api.post(`/proposal-orders/${id}/whatsapp-check/${proposalId}`, null, {
        params: { checked: newValue },
      });
      setOrder(data.order);
      if (data.lead_moved) {
        toast.success(i18n.t('orders.allConfirmed', '✓ Tutte le proposte confermate — lead spostato a "Proposta ricevuta"'));
      } else if (newValue) {
        toast.success(i18n.t('orders.waMarked', 'WhatsApp segnato come inviato'));
      } else {
        toast.success(i18n.t('orders.waReopened', 'WhatsApp riaperto'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || i18n.t('orders.waError', 'Errore aggiornamento WhatsApp'));
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!order) return null;

  const meta = statusMeta(order.status);
  const canEdit = !!order.can_edit;
  const items = order.items || [];

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="proposal-order-detail-page">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => nav('/app/proposal-orders')} data-testid="order-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" />{i18n.t('orders.backToOrders', 'Torna agli ordini')}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-xl font-serif">
                {i18n.t('orders.orderFor', 'Ordine per')} <Link to={`/app/leads/${order.lead_id}`} className="underline text-[hsl(var(--primary))]">{order.lead_name}</Link>
              </CardTitle>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{order.email_to}</span>
                <span className="flex items-center gap-1"><User className="w-3 h-3" />{i18n.t('orders.createdBy', 'Creato da')} {order.created_by_email}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(order.created_at).toLocaleString(dtLocale())}</span>
              </div>
            </div>
            <Badge variant="outline" className={`text-xs ${meta.className}`}>{meta.label}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">{i18n.t('orders.countriesSelected', 'Paesi selezionati')}</div>
              <div>{(order.countries_selected || []).map((c) => COUNTRY_LABEL[c] || c).join(', ') || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">{i18n.t('orders.pathsSelected', 'Path selezionati')}</div>
              <div>{(order.paths_selected || []).map((p) => PATH_LABEL[p] || p).join(', ') || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">{i18n.t('common.total', 'Totale')}</div>
              <div className="text-lg font-bold text-emerald-700 flex items-center gap-1">
                <Euro className="w-4 h-4" />{(order.total_price_eur || 0).toLocaleString(dtLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {!canEdit && (
        <Alert className="border-amber-300 bg-amber-50/40">
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>{i18n.t('orders.viewOnly', 'Solo visualizzazione')}</AlertTitle>
          <AlertDescription>
            {i18n.t('orders.viewOnlyDesc', 'Gli admin CEO possono evadere tutti gli ordini. I Senior possono evadere completamente i propri ordini (email, allegati e WhatsApp).')}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <h2 className="text-lg font-serif text-[hsl(var(--primary))]">
          {i18n.t('orders.includedProposals', 'Proposte incluse')} ({items.length})
        </h2>
        {items.map((it) => (
          <Card key={it.proposal_id} className="border-l-4" style={{ borderLeftColor: it.email_status === 'sent' && it.whatsapp_done ? '#16a34a' : '#f59e0b' }} data-testid={`order-item-${it.proposal_id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex-1 min-w-[240px]">
                  <div className="font-semibold">{it.name}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px]"><MapPin className="w-3 h-3 mr-1" />{COUNTRY_LABEL[it.country] || it.country}</Badge>
                    <Badge variant="outline" className="text-[10px]"><Building2 className="w-3 h-3 mr-1" />{PATH_LABEL[it.path] || it.path}</Badge>
                    <span className="text-sm font-bold text-emerald-700 flex items-center gap-0.5">
                      <Euro className="w-3 h-3" />{(it.price_eur || 0).toLocaleString(dtLocale())}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 italic">{it.email_subject}</div>
                </div>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setPreviewItem(it)} data-testid={`order-item-preview-${it.proposal_id}`}>
                  <Eye className="w-4 h-4 mr-1" />{i18n.t('orders.preview', 'Anteprima')}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t border-border/70">
                {/* Email row */}
                <div className="flex items-center gap-2 p-2 rounded-md bg-slate-50" data-testid={`order-item-email-row-${it.proposal_id}`}>
                  <div className="w-8 h-8 rounded-full bg-white border flex items-center justify-center">
                    {it.email_status === 'sent'
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      : it.email_status === 'failed'
                        ? <XCircle className="w-4 h-4 text-red-500" />
                        : <Mail className="w-4 h-4 text-slate-400" />}
                  </div>
                  <div className="flex-1 text-xs">
                    <div className="font-semibold">Email</div>
                    <div className="text-muted-foreground">
                      {it.email_status === 'sent'
                        ? `${i18n.t('orders.sentBy', 'Inviata da')} ${it.email_sent_by || '—'} — ${it.email_sent_at ? new Date(it.email_sent_at).toLocaleString(dtLocale()) : ''}`
                        : it.email_status === 'failed'
                          ? i18n.t('orders.sendFailed', 'Invio fallito — riprova')
                          : i18n.t('orders.awaitingSend', 'In attesa di invio')}
                    </div>
                  </div>
                  {canEdit && it.email_status !== 'sent' && (
                    <Button
                      size="sm"
                      className="h-8"
                      onClick={() => openSendDialog(it)}
                      disabled={busy === it.proposal_id}
                      data-testid={`order-send-email-btn-${it.proposal_id}`}
                    >
                      {busy === it.proposal_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                      {i18n.t('orders.send', 'Invia')}
                    </Button>
                  )}
                </div>

                {/* WhatsApp row */}
                <div className="flex items-center gap-2 p-2 rounded-md bg-slate-50" data-testid={`order-item-wa-row-${it.proposal_id}`}>
                  <div className="w-8 h-8 rounded-full bg-white border flex items-center justify-center">
                    {it.whatsapp_done
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      : <MessageCircle className="w-4 h-4 text-slate-400" />}
                  </div>
                  <div className="flex-1 text-xs">
                    <div className="font-semibold">WhatsApp</div>
                    <div className="text-muted-foreground">
                      {it.whatsapp_done
                        ? `${i18n.t('orders.confirmedBy', 'Confermato da')} ${it.whatsapp_done_by || '—'} — ${it.whatsapp_done_at ? new Date(it.whatsapp_done_at).toLocaleString(dtLocale()) : ''}`
                        : i18n.t('orders.waToConfirm', 'Da confermare (dopo aver scritto al cliente su WhatsApp)')}
                    </div>
                  </div>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant={it.whatsapp_done ? 'outline' : 'default'}
                      className="h-8"
                      onClick={() => toggleWhatsapp(it.proposal_id, it.whatsapp_done)}
                      disabled={busy === it.proposal_id + '-wa'}
                      data-testid={`order-wa-toggle-${it.proposal_id}`}
                    >
                      {busy === it.proposal_id + '-wa' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                      {it.whatsapp_done ? i18n.t('orders.reopen', 'Riapri') : i18n.t('orders.confirmed', 'Confermato')}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {order.notes && (
        <Card className="bg-slate-50/60">
          <CardContent className="p-3 text-sm">
            <div className="text-xs text-muted-foreground uppercase mb-1">{i18n.t('orders.notes', 'Note')}</div>
            {order.notes}
          </CardContent>
        </Card>
      )}

      {/* Preview email dialog */}
      <Dialog open={!!previewItem} onOpenChange={(v) => !v && setPreviewItem(null)}>
        <DialogContent className="max-w-2xl" data-testid="order-item-preview-dialog">
          <DialogHeader>
            <DialogTitle>{previewItem?.name}</DialogTitle>
            <DialogDescription>
              {i18n.t('orders.subject', 'Oggetto:')} <span className="font-medium">{previewItem?.email_subject}</span>
            </DialogDescription>
          </DialogHeader>
          <SafeHtml
            html={previewItem?.email_body_html || ''}
            className="prose prose-sm max-w-none border rounded-md p-4 bg-white overflow-auto max-h-[420px]"
          />
        </DialogContent>
      </Dialog>

      {/* ★ 2026-08-04 (Fase 33.2) — Send Email dialog con PDF override */}
      <Dialog open={!!sendDialog} onOpenChange={(v) => !v && setSendDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="order-send-email-dialog">
          <DialogHeader>
            <DialogTitle>{i18n.t('orders.confirmSendTitle', 'Conferma invio email')}</DialogTitle>
            <DialogDescription>
              {i18n.t('orders.proposalLabel', 'Proposta:')} <span className="font-medium">{sendDialog?.name}</span><br />
              {i18n.t('orders.recipient', 'Destinatario:')} <span className="font-mono text-xs">{order?.email_to}</span><br />
              <span className="text-[11px] text-muted-foreground">
                📧 {i18n.t('orders.cc', 'In copia (CC):')} <span className="font-mono">commerciale@housecashflows.com</span>
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            {(() => {
              // ★ 2026-08-10 (Fase 36) — Riassunto allegati che verranno inviati
              const libPdfs = sendDialog?.library_pdfs || [];
              const libLinks = sendDialog?.library_ext_links || [];
              const adhocPdfs = sendDialog?.adhoc_pdfs || [];
              const adhocLinks = sendDialog?.adhoc_ext_links || [];
              // legacy singoli (retro-compat pre-Fase36)
              const legacyLibPdf = sendDialog?.library_pdf_url;
              const legacyLibLink = sendDialog?.library_pdf_ext_link;
              const legacyAdhocPdf = sendDialog?.adhoc_pdf_url;
              const legacyAdhocLink = sendDialog?.adhoc_pdf_ext_link;
              const totalAttachments =
                libPdfs.length + adhocPdfs.length +
                (legacyLibPdf ? 1 : 0) + (legacyAdhocPdf ? 1 : 0);
              const totalLinks =
                libLinks.length + adhocLinks.length +
                (legacyLibLink ? 1 : 0) + (legacyAdhocLink ? 1 : 0);
              return (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-xs text-slate-700" data-testid="send-dialog-summary">
                  <b>{i18n.t('orders.willBeSent', 'Verranno inviati:')}</b> {i18n.t('orders.pdfCount', '{{count}} PDF allegati', { count: totalAttachments })} · {i18n.t('orders.nBodyLinks', '{{count}} link nel corpo', { count: totalLinks })}
                </div>
              );
            })()}

            {/* Sezione LIBRERIA (read-only, mostra cosa arriverà) */}
            {((sendDialog?.library_pdfs || []).length > 0 || sendDialog?.library_pdf_url) && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold mb-1">{i18n.t('orders.libraryAttachments', 'Allegati di libreria')}</div>
                <div className="space-y-1.5">
                  {(sendDialog?.library_pdfs || []).map((p) => (
                    <div key={p.id} className="flex items-center gap-2 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
                      <FileText className="w-4 h-4 text-emerald-700 shrink-0" />
                      <div className="flex-1 truncate">
                        <div className="font-medium truncate">{p.filename || 'proposta.pdf'}</div>
                        {p.size && <div className="text-[10px] text-muted-foreground">{(p.size / 1024 / 1024).toFixed(2)} MB</div>}
                      </div>
                    </div>
                  ))}
                  {sendDialog?.library_pdf_url && (sendDialog?.library_pdfs || []).length === 0 && (
                    <div className="flex items-center gap-2 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
                      <FileText className="w-4 h-4 text-emerald-700 shrink-0" />
                      <div className="flex-1 truncate">
                        <div className="font-medium truncate">{sendDialog.library_pdf_filename || 'proposta.pdf'}</div>
                        <div className="text-[10px] text-muted-foreground">(legacy singolo)</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {((sendDialog?.library_ext_links || []).length > 0 || sendDialog?.library_pdf_ext_link) && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-sky-800 font-semibold mb-1">{i18n.t('orders.libraryLinks', 'Link libreria (nel corpo email)')}</div>
                <div className="space-y-1.5">
                  {(sendDialog?.library_ext_links || []).map((el) => (
                    <div key={el.id} className="flex items-center gap-2 p-2 rounded-md bg-sky-50 border border-sky-200 text-sm">
                      <LinkIcon className="w-4 h-4 text-sky-700 shrink-0" />
                      <span className="flex-1 truncate">{el.url}</span>
                    </div>
                  ))}
                  {sendDialog?.library_pdf_ext_link && (sendDialog?.library_ext_links || []).length === 0 && (
                    <div className="flex items-center gap-2 p-2 rounded-md bg-sky-50 border border-sky-200 text-sm">
                      <LinkIcon className="w-4 h-4 text-sky-700 shrink-0" />
                      <span className="flex-1 truncate">{sendDialog.library_pdf_ext_link}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sezione AD-HOC (con delete) */}
            <div className="border-t pt-3">
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">{i18n.t('orders.adhocTitle', 'Allegati ad-hoc per questo invio')}</div>
              <p className="text-[11px] text-muted-foreground mb-2">
                {i18n.t('orders.adhocHint', 'Aggiungi documenti specifici solo per questa email. Si sommano a quelli di libreria (limite totale 20 MB in allegato binario).')}
              </p>
              {(sendDialog?.adhoc_pdfs || []).length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {(sendDialog.adhoc_pdfs).map((p) => (
                    <div key={p.id} className="flex items-center gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-sm">
                      <FileText className="w-4 h-4 text-amber-700 shrink-0" />
                      <div className="flex-1 truncate">
                        <div className="font-medium truncate">{p.filename}</div>
                        {p.size && <div className="text-[10px] text-muted-foreground">{(p.size / 1024 / 1024).toFixed(2)} MB · ad-hoc</div>}
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => removeAdhocItem(sendDialog.proposal_id, p.id)}
                        disabled={pdfBusy}
                        className="text-red-600 h-7 w-7 p-0"
                        aria-label={i18n.t('orders.removeFile', 'Rimuovi {{name}}', { name: p.filename })}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {sendDialog?.adhoc_pdf_url && (
                <div className="flex items-center gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-sm mb-2">
                  <FileText className="w-4 h-4 text-amber-700 shrink-0" />
                  <div className="flex-1 truncate">
                    <div className="font-medium truncate">{sendDialog.adhoc_pdf_filename || 'proposta.pdf'}</div>
                    <div className="text-[10px] text-muted-foreground">ad-hoc (legacy singolo)</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeAdhocItem(sendDialog.proposal_id, 'legacy')} disabled={pdfBusy} className="text-red-600 h-7 w-7 p-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
              {(sendDialog?.adhoc_ext_links || []).length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {(sendDialog.adhoc_ext_links).map((el) => (
                    <div key={el.id} className="flex items-center gap-2 p-2 rounded-md bg-sky-100 border border-sky-300 text-sm">
                      <LinkIcon className="w-4 h-4 text-sky-800 shrink-0" />
                      <span className="flex-1 truncate">{el.url}</span>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => removeAdhocItem(sendDialog.proposal_id, el.id)}
                        disabled={pdfBusy}
                        className="text-red-600 h-7 w-7 p-0"
                        aria-label={i18n.t('orders.removeLink', 'Rimuovi link')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <label className="flex items-center gap-2 p-2 rounded-md border border-dashed text-sm cursor-pointer hover:bg-slate-50 mb-2">
                <Upload className="w-4 h-4 text-slate-500" />
                <span className="flex-1 truncate">
                  {pdfBusy ? i18n.t('common.loading', 'Caricamento…') : (adhocFiles.length > 0 ? i18n.t('orders.filesSelected', '{{count}} file selezionati (verranno caricati subito)', { count: adhocFiles.length }) : i18n.t('orders.addAdhocPdf', 'Aggiungi PDF ad-hoc (uno o più, max 10 MB ciascuno)'))}
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  multiple
                  className="hidden"
                  disabled={pdfBusy}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setAdhocFiles(files);
                    // Upload immediato di ognuno
                    files.forEach((f) => uploadAdhocNow(sendDialog.proposal_id, f));
                    e.target.value = '';
                  }}
                  data-testid="order-send-adhoc-upload"
                />
              </label>

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] uppercase">{i18n.t('orders.addAdhocLink', 'Aggiungi link esterno ad-hoc')}</Label>
                  <Input
                    value={adhocLinkInput}
                    onChange={(e) => setAdhocLinkInput(e.target.value)}
                    placeholder="https://drive.google.com/…"
                    className="mt-1 h-9"
                    data-testid="order-send-adhoc-link"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAdhocLinkNow(sendDialog.proposal_id); } }}
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pdfBusy || !adhocLinkInput.trim()}
                  onClick={() => addAdhocLinkNow(sendDialog.proposal_id)}
                  className="h-9"
                  data-testid="order-send-adhoc-link-add"
                >
                  {i18n.t('orders.add', 'Aggiungi')}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(null)} disabled={busy === sendDialog?.proposal_id}>
              {i18n.t('common.cancel', 'Annulla')}
            </Button>
            <Button
              onClick={() => sendDialog && sendEmail(sendDialog.proposal_id)}
              disabled={busy === sendDialog?.proposal_id}
              data-testid="order-send-email-confirm"
            >
              {busy === sendDialog?.proposal_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {i18n.t('orders.sendNow', 'Invia ora')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
